import React from "react";
import LegalLayout, { LegalSection } from "../components/LegalLayout";
import { brand } from "../mock";

const TermsConditions = () => {
  return (
    <LegalLayout
      eyebrow="Legal"
      title={
        <>
          Terms &amp; <span className="italic text-[#daae9c]">Conditions</span>
        </>
      }
      lastUpdated="11 February 2026"
      intro={`These terms set out the agreement between you (the “Client”) and ${brand.name} (“we”, “us”, “our”) when you book a cleaning service with us. Please read them carefully — by booking, you accept these terms. Nothing in these terms limits your rights under the Australian Consumer Law (ACL).`}
      seoTitle="Terms & Conditions | The Sterling Touch"
      seoDescription="The terms and conditions that govern bookings with The Sterling Touch — Brisbane's premium residential cleaning service."
      path="/terms-and-conditions"
    >
      <LegalSection heading="1. Quotes & bookings">
        <p>
          All quotes are based on the information you provide (suburb, property
          size, condition, type of clean and any extras). If the property turns
          out to be materially different on the day — for example significantly
          larger, in heavier-than-described condition, or requiring additional
          rooms — we may need to revise the quote. We will always confirm any
          change with you before continuing.
        </p>
        <p>
          A booking is confirmed when you receive a written confirmation from us
          (email or SMS).
        </p>
      </LegalSection>

      <LegalSection heading="2. Payment">
        <p>
          Payment is due on or before the day of service unless otherwise agreed
          in writing. We accept bank transfer and major card payments. Recurring
          clients may be invoiced on a regular cycle.
        </p>
        <p>
          Overdue invoices may incur a reasonable late-payment fee. We may pause
          future bookings while an invoice is unpaid.
        </p>
      </LegalSection>

      <LegalSection heading="3. Cancellations & rescheduling">
        <p>
          We hold time exclusively for you, so a fair notice policy keeps prices
          down for everyone:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>More than 48 hours’ notice:</strong> reschedule or cancel
            free of charge.
          </li>
          <li>
            <strong>24–48 hours’ notice:</strong> a 50% fee of the booking value
            may apply.
          </li>
          <li>
            <strong>Less than 24 hours’ notice or lock-outs:</strong> the full
            booking value may be charged, as our team has already been
            scheduled.
          </li>
        </ul>
        <p>
          We understand life happens — if there’s an emergency, get in touch and
          we’ll always do our best to help.
        </p>
      </LegalSection>

      <LegalSection heading="4. Access & condition of the property">
        <p>
          You agree to provide safe and timely access to the property, working
          power, hot and cold water, and a parking space within reasonable
          distance. If paid parking is the only option, the cost may be added to
          your invoice.
        </p>
        <p>
          The Client is responsible for securing valuables, fragile items and
          confidential documents prior to the clean.
        </p>
      </LegalSection>

      <LegalSection heading="5. What is and isn’t included">
        <p>
          The scope of work is the package you’ve booked (Express Reset,
          Sanctuary Reset, Presentation, Deep Clean or Bond Clean) plus any
          extras agreed in writing. Anything outside that scope — including but
          not limited to active biological waste, heavy mould remediation,
          hoarding-level clean-outs, pests/rodent waste or exterior surfaces —
          is not included unless quoted separately.
        </p>
        <p>
          For health and safety reasons our cleaners cannot use ladders above
          standard step-stool height, lift heavy furniture alone, or handle
          hazardous substances.
        </p>
      </LegalSection>

      <LegalSection heading="6. Bond / end-of-lease cleans">
        <p>
          Our Bond Clean follows a comprehensive checklist designed to meet most
          property-manager standards. If your agent requests rework within{" "}
          <strong>72 hours of the clean</strong> against an item on our
          checklist, we will return and re-clean that item at no extra cost,
          provided no further use of the property has occurred and access is
          provided promptly.
        </p>
        <p>
          Bond cleans assume the property is empty, utilities are connected, and
          carpets/pests are arranged separately unless quoted. Walls, mould and
          existing damage are not part of a standard bond clean.
        </p>
      </LegalSection>

      <LegalSection heading="7. Satisfaction guarantee">
        <p>
          If you’re not satisfied with any part of a regular clean, please let
          us know within <strong>24 hours</strong> and we’ll return to put it
          right at no charge. We can’t offer this if we’re notified after 24
          hours, after the area has been used further, or if the issue is
          outside the agreed scope.
        </p>
      </LegalSection>

      <LegalSection heading="8. Damage, breakage & liability">
        <p>
          We treat every home with the care of our own. Our team is fully
          insured and police-checked. In the unlikely event something is
          accidentally damaged, please tell us within 48 hours so we can
          investigate and resolve it fairly.
        </p>
        <p>
          To the extent permitted by law, our maximum liability for any loss or
          damage is limited to the value of the booking giving rise to the
          claim. We are not liable for damage caused by pre-existing wear and
          tear, faulty fittings, items not properly secured, or by the Client’s
          own products or appliances.
        </p>
        <p>
          Nothing in these terms excludes any consumer guarantee or right under
          the Australian Consumer Law that cannot be lawfully excluded.
        </p>
      </LegalSection>

      <LegalSection heading="9. Team & conduct">
        <p>
          Our team are professionals and deserve a safe, respectful working
          environment. We reserve the right to leave a property and charge the
          full booking value if our team is exposed to unsafe conditions,
          harassment or inappropriate behaviour.
        </p>
        <p>
          We do not permit solicitation of our team for private work outside
          this agreement. If you’d like to engage one of our cleaners directly,
          a placement fee equal to 4 weeks of regular service applies.
        </p>
      </LegalSection>

      <LegalSection heading="10. Privacy">
        <p>
          We handle your personal information in accordance with our{" "}
          <a
            href="/privacy-policy"
            className="text-[#b8866e] underline underline-offset-2"
          >
            Privacy Policy
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="11. Changes to these terms">
        <p>
          We may update these terms occasionally. The version published at{" "}
          <span className="text-ink">/terms-and-conditions</span> at the time of
          your booking is the version that applies. Material changes affecting
          existing bookings will be communicated to you directly.
        </p>
      </LegalSection>

      <LegalSection heading="12. Governing law">
        <p>
          These terms are governed by the laws of Queensland, Australia. Any
          dispute will be handled in the courts of Queensland.
        </p>
      </LegalSection>

      <LegalSection heading="13. Get in touch">
        <p>
          Anything unclear? Email{" "}
          <a
            href={`mailto:${brand.email}`}
            className="text-[#b8866e] underline underline-offset-2"
          >
            {brand.email}
          </a>{" "}
          or call{" "}
          <a
            href={`tel:${brand.phone.replace(/\s/g, "")}`}
            className="text-[#b8866e] underline underline-offset-2"
          >
            {brand.phone}
          </a>{" "}
          — we’re happy to walk you through it.
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export default TermsConditions;
